import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import client, { mediaVariant } from "../lib/catalogueApi";
import { attachTyping, STATIC_PLACEHOLDER } from "./typingPlaceholder";

const cache = new Map();
const norm = (q) => q.trim().toLowerCase().replace(/\s+/g, " ");

function Hi({ text, q }) {
  const ws = norm(q).split(" ").filter((w) => w.length > 1).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!ws.length) return text;
  const parts = String(text).split(new RegExp(`(${ws.join("|")})`, "ig"));
  return parts.map((p, i) => (i % 2 ? <mark key={i} className="bg-transparent font-extrabold text-[var(--brand-primary)]">{p}</mark> : p));
}

const money = (v) => (v == null ? "" : `$${Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// T12.c header type-ahead: 150 ms debounce, client LRU, aborts stale requests, keyboard + screen-reader friendly
export function SearchSuggest({ inputClass, testid = "header-search", big = false }) {
  const nav = useNavigate();
  const lid = useId();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const [act, setAct] = useState(-1);
  const ctl = useRef(null), box = useRef(null), inp = useRef(null);
  useEffect(() => attachTyping(inp.current), []);  // T75: animated overlay placeholder (scrolls; never cut off)
  useEffect(() => {
    const k = norm(q);
    if (k.length < 2) { setRes(null); return undefined; }
    if (cache.has(k)) { setRes(cache.get(k)); return undefined; }
    const t = setTimeout(() => {
      ctl.current?.abort();
      ctl.current = new AbortController();
      client.get("/catalogue/suggest", { params: { q: k }, signal: ctl.current.signal }).then((r) => {
        cache.set(k, r.data);
        if (cache.size > 60) cache.delete(cache.keys().next().value);
        setRes(r.data);
      }).catch(() => {});
    }, 150);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const off = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, []);
  const rows = res ? [
    ...res.products.map((p) => ({ id: `p-${p.slug}`, to: `/p/${p.slug}`, p })),
    ...res.brands.map((b) => ({ id: `b-${b.slug}`, to: `/b/${b.slug}`, label: b.name, kind: "Brand" })),
    ...res.categories.map((c) => ({ id: `c-${c.slug}`, to: `/c/${c.slug}`, label: c.name.toLowerCase(), kind: c.parent ? `in ${c.parent.toLowerCase()}` : "Category", count: c.count })),
    { id: "all", to: `/search?q=${encodeURIComponent(q.trim())}`, label: `See all results for “${q.trim()}”`, all: true },
  ] : [];
  const go = (to) => { setOpen(false); setAct(-1); nav(to); };
  const onKey = (e) => {
    if (e.key === "ArrowDown" && rows.length) { e.preventDefault(); setOpen(true); setAct((a) => (a + 1) % rows.length); }
    else if (e.key === "ArrowUp" && rows.length) { e.preventDefault(); setAct((a) => (a <= 0 ? rows.length - 1 : a - 1)); }
    else if (e.key === "Escape") { setOpen(false); setAct(-1); }
  };
  const submit = (e) => { e.preventDefault(); if (act >= 0 && rows[act]) go(rows[act].to); else if (q.trim()) go(`/search?q=${encodeURIComponent(q.trim())}`); };
  const show = open && rows.length > 0;
  return (
    <form onSubmit={submit} className={`relative flex-1 ${big ? "" : "max-w-xl"}`} role="search" ref={box}>
      <input ref={inp} data-testid={`${testid}-input`} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); setAct(-1); }} onFocus={() => setOpen(true)} onKeyDown={onKey}
        placeholder={STATIC_PLACEHOLDER} aria-label="Search products" role="combobox" aria-expanded={show} aria-controls={lid} aria-autocomplete="list"
        aria-activedescendant={act >= 0 && rows[act] ? `${lid}-${rows[act].id}` : undefined} autoComplete="off" className={inputClass} />
      <button data-testid={`${testid}-submit`} type="submit" aria-label="Search"
        className={big ? "absolute right-0 top-0 bottom-0 inline-flex min-h-[44px] items-center gap-1.5 rounded-r-xl bg-[var(--brand-button)] px-4 text-sm font-bold text-[var(--brand-on-button)] transition-colors hover:bg-[var(--brand-button-hover)] active:bg-[var(--brand-button-active)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-focus)]"
          : "absolute right-1.5 top-1.5 rounded-full bg-[var(--brand-button)] p-1.5 transition-colors hover:bg-[var(--brand-button-hover)]"}>
        <Search size={big ? 17 : 16} className="text-[var(--brand-on-button)]" aria-hidden="true" />{big && <span className="hidden sm:inline">Search</span>}
      </button>
      {show && (
        <ul id={lid} role="listbox" data-testid="search-suggest" className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[70vh] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-1.5 text-[var(--brand-primary)] shadow-2xl">
          {rows.map((r, i) => (
            <li key={r.id} id={`${lid}-${r.id}`} role="option" aria-selected={i === act} data-testid={`suggest-${r.id}`}
              onMouseDown={(e) => { e.preventDefault(); go(r.to); }} onMouseEnter={() => setAct(i)}
              className={`flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 ${i === act ? "bg-[var(--brand-primary-tint)]" : ""} ${r.all ? "mt-1 border-t border-slate-100 font-bold text-[var(--brand-link)]" : ""} ${r.kind && (i === 0 || rows[i - 1].p) ? "mt-1 border-t border-slate-100" : ""}`}>
              {r.p ? (
                <>
                  <span className="h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-slate-100 bg-white">
                    {r.p.media_id ? <img src={mediaVariant(r.p.media_id, 160)} alt="" width="44" height="44" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                      : <Search size={18} className="m-auto mt-3 text-slate-300" aria-hidden="true" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm"><Hi text={r.p.name} q={q} /></span>
                    <span className="text-xs text-slate-500">{r.p.brand}{r.p.formerly && <> · <span data-testid={`suggest-formerly-${r.p.slug}`} className="font-semibold text-slate-600">{r.p.formerly}</span></>} · <span className={r.p.is_sellable ? "text-green-700" : "text-slate-400"}>{r.p.is_sellable ? "In stock" : "Out of stock"}</span></span>
                  </span>
                  <span className="shrink-0 text-sm font-extrabold tabular-nums">{money(r.p.price_inc_gst)}</span>
                </>
              ) : (
                <span className="flex w-full items-center gap-2 text-sm">
                  <span className={r.all ? "" : "capitalize"}><Hi text={r.label} q={q} /></span>
                  {r.kind && <span className="text-xs text-slate-400">{r.kind}</span>}
                  {r.count != null && <span className="ml-auto text-xs tabular-nums text-slate-400">{r.count.toLocaleString("en-AU")}</span>}
                </span>
              )}
            </li>))}
        </ul>)}
    </form>
  );
}
